/**
 * Who may edit an enrolment — the « Fréquentation » window and the « Fréquentations » list's
 * actions. Mirrors the route gate of the writes on the server (Administrateur, Superviseur).
 *
 * Since #1443 a teacher opens the enrolment of one of their own students, read-only, from the
 * student card of Présences — the server's row scope: their groups and assignments, a chef's whole
 * programs. The window opens with its fields disabled.
 */
const FREQUENTATION_WRITER_PERMISSIONS = [
    'Parcours.LearningModule.Administrateur',
    'Parcours.LearningModule.Superviseur',
] as const;

const FREQUENTATION_READER_PERMISSIONS = [
    ...FREQUENTATION_WRITER_PERMISSIONS,
    'Parcours.LearningModule.Enseignant',
] as const;

export function canEditFrequentation(permissions: readonly string[]): boolean {
    return FREQUENTATION_WRITER_PERMISSIONS.some(p => permissions.includes(p));
}

export function canOpenFrequentation(permissions: readonly string[]): boolean {
    return FREQUENTATION_READER_PERMISSIONS.some(p => permissions.includes(p));
}

/** The teacher pages whose student card opens an enrolment (#1443), by route name. */
export const TEACHER_ENROLMENT_ORIGINS = ['enseignement-presences', 'enseignement-assiduite'] as const;
export type TeacherEnrolmentOrigin = (typeof TEACHER_ENROLMENT_ORIGINS)[number];

/**
 * The page a teacher opened the enrolment from (the card says it in `from`), so the window leads
 * back to it — Présences or Assiduité; Présences when the link did not say.
 */
export function teacherEnrolmentOrigin(from: unknown): TeacherEnrolmentOrigin {
    return TEACHER_ENROLMENT_ORIGINS.find(name => name === from) ?? 'enseignement-presences';
}

/**
 * Whether the student card offers the enrolment window and its printable sheet for the selected
 * enrolment. Staff: any. A teacher: only the enrolment the card was opened on from a Présences row,
 * the one their row scope covers; another enrolment of the same student may belong to a program
 * they do not teach, and the server refuses it.
 */
export function canOpenFromStudentCard(
    permissions: readonly string[],
    openedOn: string | undefined,
    selected: string | undefined,
): boolean {
    if (canEditFrequentation(permissions)) return true;
    return canOpenFrequentation(permissions) && !!openedOn && openedOn === selected;
}
